import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Trash2, Ticket, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith('/') ? `${API_URL}${u}` : u);

export const BroadcastHeaderImage = ({ adminFetch, imageUrl, setImageUrl, link, setLink }) => {
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await adminFetch(`${API_URL}/api/admin/broadcast/upload-image`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload failed');
      setImageUrl(d.image_url);
      toast.success('Header image added');
    } catch (e) { toast.error(e.message); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="broadcast-header-image-section">
      <Label className="text-sm inline-flex items-center gap-1"><ImagePlus className="h-4 w-4 text-teal-600" /> Header image (optional)</Label>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="broadcast-header-image-input" />
      {imageUrl ? (
        <div className="space-y-2">
          <img src={imgSrc(imageUrl)} alt="Header" className="w-full max-w-[600px] rounded-lg border" data-testid="broadcast-header-image-preview" />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="broadcast-header-image-replace">
              {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1" />} Replace
            </Button>
            <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => setImageUrl('')} data-testid="broadcast-header-image-remove"><Trash2 className="h-4 w-4 mr-1" /> Remove</Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full border-2 border-dashed border-slate-300 rounded-lg py-6 text-sm text-slate-500 hover:border-teal-500 hover:text-teal-700 transition-colors" data-testid="broadcast-header-image-upload">
          {uploading ? <Loader2 className="h-5 w-5 mx-auto animate-spin" /> : <>Click to upload a banner (e.g. your sale graphic) · 600px wide works best</>}
        </button>
      )}
      <div>
        <Label className="text-xs text-slate-500">When clicked, open this link</Label>
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder={`${window.location.origin}/products`} data-testid="broadcast-header-link" />
        <p className="text-xs text-slate-400 mt-1">Leave blank to link to your shop.</p>
      </div>
    </div>
  );
};

const emptyCode = () => ({ code: '', discount_type: 'percentage', discount_value: '', expires_at: '', once_per_customer: true });

export const BroadcastDiscountCode = ({ adminFetch, value, setValue, unique, setUnique }) => {
  const [codes, setCodes] = React.useState([]);
  const [creating, setCreating] = React.useState(false);
  const [form, setForm] = React.useState(emptyCode());
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await adminFetch(`${API_URL}/api/admin/discount-codes`);
      const d = await r.json();
      if (r.ok) setCodes((d || []).filter((c) => c.is_active !== false));
    } catch (_) {}
  }, [adminFetch]);
  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!(parseFloat(form.discount_value) > 0)) { toast.error('Enter a discount amount'); return; }
    setSaving(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/discount-codes`, {
        method: 'POST',
        body: JSON.stringify({ ...form, discount_value: parseFloat(form.discount_value), expires_at: form.expires_at || null, description: 'Broadcast promo', created_for: 'Broadcast' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Code ${d.code.code} created`);
      await load();
      setValue(d.code.code);
      setCreating(false); setForm(emptyCode());
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const sel = codes.find((c) => c.code === value);

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="broadcast-discount-section">
      <Label className="text-sm inline-flex items-center gap-1"><Ticket className="h-4 w-4 text-teal-600" /> Discount code (optional)</Label>
      <div className="flex flex-col sm:flex-row gap-2">
        <select value={value} onChange={(e) => setValue(e.target.value)} className="w-full sm:flex-1 border rounded-md h-9 px-2 text-sm bg-white" data-testid="broadcast-discount-select">
          <option value="">No discount code</option>
          {codes.map((c) => (
            <option key={c.code_id} value={c.code}>
              {c.code} — {c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value}`} off{c.once_per_customer ? ' · once per customer' : ''}
            </option>
          ))}
        </select>
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => setCreating((v) => !v)} data-testid="broadcast-discount-new-btn"><Plus className="h-4 w-4 mr-1" /> New code</Button>
      </div>
      {value && (
        <label className="flex items-start gap-2 rounded-lg bg-teal-50 border border-teal-100 p-3 cursor-pointer" data-testid="broadcast-unique-codes-label">
          <input type="checkbox" checked={unique} onChange={(e) => setUnique(e.target.checked)} className="h-4 w-4 mt-0.5 accent-teal-600" data-testid="broadcast-unique-codes" />
          <span className="text-sm text-slate-700">
            <strong>Give each person their own code</strong>
            <span className="block text-xs text-slate-500">Everyone gets a unique code like {(value || '').replace(/[^A-Z0-9]/gi, '').slice(0, 10)}-7K2Q9P with the same discount. Each code works once, and only on that customer's account. If the code has an expiry date, anyone who hasn't used theirs gets an automatic reminder email in the last 24 hours.</span>
          </span>
        </label>
      )}
      {sel && !sel.once_per_customer && !unique && (
        <p className="text-xs text-amber-600" data-testid="broadcast-discount-reuse-warning">Heads up: this code can be used more than once per customer. Turn on "one use per customer" in Discount Codes, or create a new code here.</p>
      )}
      {creating && (
        <div className="rounded-lg bg-slate-50 border p-3 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="broadcast-discount-create-form">
          <div>
            <Label className="text-xs">Code</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SALE20 (auto if blank)" data-testid="broadcast-new-code" />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="w-full border rounded-md h-9 px-2 text-sm bg-white" data-testid="broadcast-new-code-type">
              <option value="percentage">% off</option>
              <option value="fixed">$ off</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Amount</Label>
            <Input type="number" min="0" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} placeholder="20" data-testid="broadcast-new-code-value" />
          </div>
          <div>
            <Label className="text-xs">Expires (optional)</Label>
            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="broadcast-new-code-expiry" />
          </div>
          <label className="sm:col-span-3 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={form.once_per_customer} onChange={(e) => setForm({ ...form, once_per_customer: e.target.checked })} className="h-4 w-4 accent-teal-600" data-testid="broadcast-new-code-once" />
            One use per customer account
          </label>
          <Button type="button" className="bg-teal-700 hover:bg-teal-800 text-white" onClick={create} disabled={saving} data-testid="broadcast-new-code-save">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Create code
          </Button>
        </div>
      )}
      <p className="text-xs text-slate-400">The code appears as a highlighted box at the bottom of the email. If the code has an expiry date, a "Sale ends in X days" line is added automatically.</p>
    </div>
  );
};
